import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StateSelect } from "@/components/StateSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { MapPin, Pencil, ChevronsUpDown, Search, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { DeleteButton } from "@/components/DeleteButton";
import { CC_ROLES } from "@/lib/constants";

const CI_DISCIPLINES = ["PT", "OT", "SLP", "PTA", "OTA"];

function StaffPicker({ people, name, email, onPick, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testId} className="w-full justify-between font-normal h-9">
          <span className="truncate">{name ? name : <span className="text-zinc-400">Search directory…</span>}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(v, s) => {
          const needle = s.trim().toLowerCase();
          if (!needle) return 1;
          // Match the START of a first name, surname or email — not letters buried mid-word.
          return v.toLowerCase().split(/[\s@._\-+]+/).some((t) => t.startsWith(needle)) ? 1 : 0;
        }}>
          <CommandInput placeholder="Search by name…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No match. (Run SharePoint sync if empty.)</CommandEmpty>
            <CommandGroup>
              {people.map((p) => (
                <CommandItem key={p.email} value={`${p.name} ${p.email}`} onSelect={() => { onPick(p); setOpen(false); }} data-testid={`${testId}-opt-${p.email}`}>
                  <div><p className="text-sm">{p.name}</p><p className="text-xs text-zinc-400">{p.email}</p></div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Facilities() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dorPeople, setDorPeople] = useState([]);
  const [amPeople, setAmPeople] = useState([]);
  const [editCis, setEditCis] = useState([]);
  const [newCi, setNewCi] = useState({ name: "", email: "", title: "", disc_str: "" });
  const [ciBusy, setCiBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const isCC = CC_ROLES.includes(user.role);
  const isFacility = user.role === "Clinical Instructor";
  const [showHidden, setShowHidden] = useState(false);
  const load = (hidden = showHidden) => api.get("/facilities", { params: hidden ? { include_hidden: true } : {} }).then(({ data }) => setRows(data));
  useEffect(() => {
    load(showHidden);
    api.get("/staff-directory?role=DOR").then(({ data }) => setDorPeople(data)).catch(() => {});
    api.get("/staff-directory?role=AM").then(({ data }) => setAmPeople(data)).catch(() => {});
  }, [showHidden]);

  const states = Array.from(new Set(rows.map((f) => (f.state || "").trim()).filter(Boolean))).sort();
  const missingStateCount = rows.filter((f) => !(f.state || "").trim()).length;
  const q = query.trim().toLowerCase();
  const filtered = rows.filter((f) => {
    if (stateFilter === "__none__") { if ((f.state || "").trim()) return false; }
    else if (stateFilter !== "all" && (f.state || "").trim() !== stateFilter) return false;
    if (!q) return true;
    const hay = [f.name, f.city, f.state, f.facility_code, f.address, f.zip, f.dor_name, f.dor_email, f.am_name, f.am_email,
      ...(f.disciplines_supported || []), ...((f.instructors || []).map((c) => c.name))].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const inviteStaff = async (title) => {
    const name = title === "DOR" ? form.dor_name : form.am_name;
    const email = title === "DOR" ? form.dor_email : form.am_email;
    if (!editing?.id) return toast.error("Save the facility first, then invite.");
    if (!name || !email) return toast.error(`Enter the ${title} name and email first.`);
    try {
      const { data } = await api.post("/users", { name, email, role: "Clinical Instructor", title,
        organization_type: "facility", organization_id: editing.id });
      toast.success(data.sso ? `${title} invited — they'll sign in with Microsoft` : `${title} invited — login emailed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm({}); setEditCis([]); setNewCi({ name: "", email: "", phone: "", discipline: "" }); setOpen(true); };
  const openEdit = (f) => {
    const cap = {};
    (f.capacity || []).forEach((c) => { cap[c.discipline] = { id: c.id, available_slots: c.available_slots }; });
    setEditing(f); setForm({ ...f, disc_str: (f.disciplines_supported || []).join(", "), cap });
    setEditCis(f.instructors || []); setNewCi({ name: "", email: "", phone: "", discipline: "" }); setOpen(true);
  };
  const reloadCis = async (facId) => {
    try { const { data } = await api.get(`/clinical-instructors?facility_id=${facId}`); setEditCis(data); } catch { /* noop */ }
    load();
  };
  const addCi = async () => {
    if (!editing?.id) return toast.error("Save the facility first, then add instructors.");
    if (!newCi.name || !newCi.email) return toast.error("Enter the instructor's name and email.");
    if (!newCi.discipline) return toast.error("Pick a discipline for this instructor.");
    setCiBusy(true);
    try {
      await api.post("/clinical-instructors", { name: newCi.name, email: newCi.email, phone: newCi.phone || undefined,
        disciplines: [newCi.discipline], facility_id: editing.id });
      toast.success("Clinical instructor added");
      setNewCi({ name: "", email: "", phone: "", discipline: "" });
      await reloadCis(editing.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCiBusy(false); }
  };
  const removeCi = async (ci) => {
    try {
      await api.delete(`/clinical-instructors/${ci.id}`);
      toast.success(`Removed ${ci.name}`);
      await reloadCis(editing.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const inviteCi = async (ci) => {
    try {
      await api.post("/users", { name: ci.name, email: ci.email, role: "Clinical Instructor", organization_id: editing.id, organization_type: "Facility" });
      toast.success(`Invite sent to ${ci.name} — they'll see just their students`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canInvite = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"].includes(user?.role);
  const toggleHidden = async (f) => {
    try {
      await api.patch(`/facilities/${f.id}/hidden`, { hidden: !f.hidden });
      toast.success(f.hidden ? `${f.name} is visible again` : `${f.name} hidden — it won't appear in any facility choices`);
      load(showHidden);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setCap = (d, val) => setForm((prev) => ({ ...prev, cap: { ...(prev.cap || {}), [d]: { ...((prev.cap || {})[d] || {}), available_slots: val } } }));

  const save = async () => {
    try {
      const payload = { name: form.name, facility_code: form.facility_code, city: form.city, state: form.state, address: form.address, zip: form.zip,
        dor_name: form.dor_name, dor_email: form.dor_email, am_name: form.am_name, am_email: form.am_email,
        rdo_name: form.rdo_name, rdo_email: form.rdo_email };
      let staff = [];
      if (editing) { const { data } = await api.patch(`/facilities/${editing.id}`, payload); staff = data.staff_linked || []; }
      else { const { data } = await api.post("/facilities", payload); staff = data.staff_linked || []; setEditing({ ...payload, id: data.id }); }
      toast.success(editing ? "Facility updated" : "Facility created");
      staff.forEach((s) => toast.success(
        `${s.role} ${s.email} ${s.created ? "was given a login and invited" : "is now linked to this site"}`
        + (s.pending_alerted ? ` — alerted about ${s.pending_alerted} request${s.pending_alerted > 1 ? "s" : ""} already waiting` : "")));
      setOpen(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Facilities" subtitle="Sites, disciplines & clinical capacity">
        {isCC && <Button onClick={openNew} className="bg-cc-blue text-white" data-testid="add-facility-btn">+ Add Facility</Button>}
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, city, code, DOR, instructor…" className="pl-9" data-testid="facility-search" />
        </div>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-44" data-testid="facility-state-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            {missingStateCount > 0 && <SelectItem value="__none__">⚠ No state set ({missingStateCount})</SelectItem>}
            {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {isCC && (
          <Button variant="outline" onClick={() => setShowHidden((v) => !v)} data-testid="toggle-hidden-facilities">
            {showHidden ? <><EyeOff className="w-4 h-4 mr-1.5" />Hide hidden facilities</> : <><Eye className="w-4 h-4 mr-1.5" />Show hidden facilities</>}
          </Button>
        )}
      </div>
      <p className="text-xs text-zinc-400 mb-3" data-testid="facility-count">{filtered.length} of {rows.length} facilities{showHidden ? " (including hidden)" : ""}</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <Card><EmptyState text="No facilities." /></Card>}
        {rows.length > 0 && filtered.length === 0 && <Card><EmptyState text="No facilities match your search." /></Card>}
        {filtered.map((f) => {
          const cis = f.instructors || [];
          const canEdit = isCC || (isFacility && f.id === user.organization_id);
          return (
            <Card key={f.id} className={`p-0 ${f.hidden ? "opacity-60" : ""}`}>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <h3 className="font-medium text-zinc-900">{f.name}
                    {f.hidden && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 bg-zinc-200 text-zinc-600 rounded align-middle" data-testid={`fac-hidden-${f.id}`}>Hidden</span>}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] px-1.5 py-0.5 bg-zinc-100 rounded">{f.facility_code}</span>
                    {canEdit && <button onClick={() => openEdit(f)} data-testid={`edit-facility-${f.id}`} className="text-zinc-400 hover:text-cc-blue"><Pencil className="w-4 h-4" /></button>}
                    {isCC && <button onClick={() => toggleHidden(f)} title={f.hidden ? "Show this facility again" : "Hide — not participating in the program"} data-testid={`toggle-hidden-${f.id}`} className="text-zinc-400 hover:text-cc-orange">{f.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>}
                    {user.role === "Super Admin" && <DeleteButton endpoint={`/facilities/${f.id}`} label="facility" name={f.name} onDeleted={load} testId={`delete-facility-${f.id}`} />}
                  </div>
                </div>
                <p className="text-sm text-zinc-500 flex items-center gap-1.5 mt-1"><MapPin className="w-3.5 h-3.5" />{f.city}{f.city && f.state ? ", " : ""}{f.state}
                  {!(f.state || "").trim() && <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded" data-testid={`fac-no-state-${f.id}`}><AlertTriangle className="w-3 h-3" />No state set</span>}
                </p>
                {(f.dor_name || f.am_name) && (
                  <div className="mt-2 space-y-0.5 text-xs text-zinc-500" data-testid={`fac-leaders-${f.id}`}>
                    {f.dor_name && <div><span className="text-zinc-400">DOR:</span> {f.dor_name}</div>}
                    {f.am_name && <div><span className="text-zinc-400">AM:</span> {f.am_name}</div>}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-3">
                  {(f.disciplines_supported || []).map((d) => <span key={d} className="text-[10px] font-mono px-1.5 py-0.5 bg-cc-blue/10 text-cc-blue rounded">{d}</span>)}
                </div>
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Clinical Instructors</span>
                    {cis.length ? <span className="font-mono font-semibold text-cc-green" data-testid={`fac-ci-count-${f.id}`}>{cis.length}</span> : <StatusBadge status="No Instructors" />}
                  </div>
                  {cis.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5" data-testid={`fac-ci-list-${f.id}`}>
                      {cis.slice(0, 6).map((c) => (
                        <li key={c.id} className="text-xs text-zinc-600 flex justify-between gap-2">
                          <span className="truncate">{c.name}{c.title ? ` · ${c.title}` : ""}</span>
                          {(c.disciplines || []).length > 0 && <span className="font-mono text-zinc-400">{(c.disciplines || []).join("/")}</span>}
                        </li>
                      ))}
                      {cis.length > 6 && <li className="text-xs text-zinc-400">+{cis.length - 6} more</li>}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Facility" : "Add Facility"}</DialogTitle>
            <DialogDescription>{editing ? "Update details, leadership and clinical instructors." : "Create a new facility site."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input data-testid="facility-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Code"><Input value={form.facility_code || ""} onChange={(e) => setForm({ ...form, facility_code: e.target.value })} /></Field>
            <Field label="City"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><StateSelect value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="facility-state" /></Field>
            <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Zip"><Input value={form.zip || ""} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></Field>
            <Field label="DOR (Director of Rehab)">
              <StaffPicker people={dorPeople} name={form.dor_name} email={form.dor_email} testId="facility-dor-picker"
                onPick={(p) => setForm({ ...form, dor_name: p.name, dor_email: p.email })} />
            </Field>
            <Field label="DOR email">
              <div className="flex gap-1">
                <Input data-testid="facility-dor-email" value={form.dor_email || ""} onChange={(e) => setForm({ ...form, dor_email: e.target.value })} />
                <Button type="button" variant="outline" size="sm" className="shrink-0" data-testid="invite-dor-btn" onClick={() => inviteStaff("DOR")}>Invite</Button>
              </div>
            </Field>
            <Field label="Area Manager (AM)">
              <StaffPicker people={amPeople} name={form.am_name} email={form.am_email} testId="facility-am-picker"
                onPick={(p) => setForm({ ...form, am_name: p.name, am_email: p.email })} />
            </Field>
            <Field label="AM email">
              <div className="flex gap-1">
                <Input data-testid="facility-am-email" value={form.am_email || ""} onChange={(e) => setForm({ ...form, am_email: e.target.value })} />
                <Button type="button" variant="outline" size="sm" className="shrink-0" data-testid="invite-am-btn" onClick={() => inviteStaff("AM")}>Invite</Button>
              </div>
            </Field>
            <Field label="Regional Director of Ops (RDO)">
              <Input data-testid="facility-rdo-name" value={form.rdo_name || ""} onChange={(e) => setForm({ ...form, rdo_name: e.target.value })} placeholder="RDO name" />
            </Field>
            <Field label="RDO email">
              <Input data-testid="facility-rdo-email" value={form.rdo_email || ""} onChange={(e) => setForm({ ...form, rdo_email: e.target.value })} placeholder="rdo@facility.com" />
            </Field>
            {editing?.id && (
              <div className="col-span-2 border-t pt-3" data-testid="ci-manage-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-zinc-800">Clinical Instructors</p>
                  <span className="text-xs text-zinc-400" data-testid="ci-manage-count">{editCis.length} assigned</span>
                </div>
                {editCis.length > 0 ? (
                  <div className="space-y-3 mb-3" data-testid="ci-manage-list">
                    {CI_DISCIPLINES.map((disc) => {
                      const group = editCis.filter((c) => (c.disciplines || []).includes(disc));
                      if (group.length === 0) return null;
                      return (
                        <div key={disc}>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-cc-blue mb-1">{disc} <span className="text-zinc-400 font-normal">({group.length})</span></p>
                          <ul className="space-y-1.5">
                            {group.map((c) => (
                              <li key={c.id} className="flex items-center justify-between gap-2 bg-zinc-50 border rounded-md px-2.5 py-1.5" data-testid={`ci-row-${c.id}`}>
                                <div className="min-w-0">
                                  <p className="text-sm text-zinc-800 truncate">{c.name}</p>
                                  <p className="text-xs text-zinc-400 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {canInvite && <Button type="button" variant="ghost" size="sm" className="text-cc-blue hover:bg-blue-50" data-testid={`ci-invite-${c.id}`} onClick={() => inviteCi(c)}>Invite</Button>}
                                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`ci-remove-${c.id}`} onClick={() => removeCi(c)}>Remove</Button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                    {editCis.some((c) => !(c.disciplines || []).some((d) => CI_DISCIPLINES.includes(d))) && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">Other</p>
                        <ul className="space-y-1.5">
                          {editCis.filter((c) => !(c.disciplines || []).some((d) => CI_DISCIPLINES.includes(d))).map((c) => (
                            <li key={c.id} className="flex items-center justify-between gap-2 bg-zinc-50 border rounded-md px-2.5 py-1.5" data-testid={`ci-row-${c.id}`}>
                              <div className="min-w-0">
                                <p className="text-sm text-zinc-800 truncate">{c.name}</p>
                                <p className="text-xs text-zinc-400 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {canInvite && <Button type="button" variant="ghost" size="sm" className="text-cc-blue hover:bg-blue-50" data-testid={`ci-invite-${c.id}`} onClick={() => inviteCi(c)}>Invite</Button>}
                                <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`ci-remove-${c.id}`} onClick={() => removeCi(c)}>Remove</Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : <p className="text-xs text-zinc-400 mb-3" data-testid="ci-manage-empty">No instructors assigned yet. Add several per discipline below.</p>}
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Name" value={newCi.name} onChange={(e) => setNewCi({ ...newCi, name: e.target.value })} data-testid="ci-new-name" />
                  <Input placeholder="Email" value={newCi.email} onChange={(e) => setNewCi({ ...newCi, email: e.target.value })} data-testid="ci-new-email" />
                  <Input placeholder="Phone" value={newCi.phone} onChange={(e) => setNewCi({ ...newCi, phone: e.target.value })} data-testid="ci-new-phone" />
                  <Select value={newCi.discipline || ""} onValueChange={(v) => setNewCi({ ...newCi, discipline: v })}>
                    <SelectTrigger data-testid="ci-new-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
                    <SelectContent>{CI_DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-2 w-full" disabled={ciBusy} onClick={addCi} data-testid="ci-add-btn">{ciBusy ? "Adding…" : "+ Add Instructor"}</Button>
              </div>
            )}
            <div className="col-span-2">
              <p className="text-xs text-zinc-500 bg-zinc-50 border rounded-md p-2" data-testid="facility-slots-note">Availability is now based on the <b>Clinical Instructors assigned</b> to this facility (invite the DOR/AM or add instructors from Users). A facility is offered to schools when it has at least one active instructor.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-cc-blue text-white" data-testid="save-facility-btn">{editing ? "Save Changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
